import * as Sentry from '@sentry/nextjs';
import { ReadonlyURLSearchParams } from "next/navigation";
import { ExtendContractFormValues } from "@/app/api/extension/extensionSchemas";
import { apiPost } from "@/common/api/api-client";
import { FlowType, URLParams } from "@/common/api/interfaces";
import { startPaymentSession } from "@/common/api/payment-session";
import { ExtensionFlowDataLayerData } from "@/common/types/datalayer.interfaces";
import { Languages } from "@/common/utils/constants";
import { Club, Maybe } from "@/generated/contentfulTypes";

type HandlePaymentType = {
    searchParams: ReadonlyURLSearchParams;
    lang: Languages;
    club?: Maybe<Club>;
    referenceText: string;
    amount: number;
};

type HandleMembershipExtensionType = {
    searchParams: ReadonlyURLSearchParams;
    formData?: ExtendContractFormValues;
    customerExtensionEndDate?: string | null;
    cancelationReasonId?: number | null;
    additionalInformationId?: number | null;
    additionalInformationValue?: string | null;
    dataLayerData: ExtensionFlowDataLayerData;
    onError?: () => void;
    club?: Maybe<Club>;
    lang: Languages
}

export const handleWithPayment = async ({
    searchParams,
    club,
    referenceText,
    lang,
    amount
}: HandlePaymentType
) => {
    try {
        const modifiedSearchParams = new URLSearchParams(searchParams.toString());
        const successfulRedirectionSlug = club?.extensionThankYouPage?.slug;
        if (successfulRedirectionSlug) {
            modifiedSearchParams.set(
                URLParams.SUCCESSFUL_EXTENSION_REDIRECT_SLUG,
                successfulRedirectionSlug
            );
        }

        await startPaymentSession({
            urlParams: modifiedSearchParams,
            pathname: window.location.pathname,
            referenceText,
            lang,
            flowType: FlowType.EXTENSION,
            amount
        });

        Sentry.withScope((scope) => {
            scope.setTag('flow_type', 'extension');
            scope.setTag('step', 'payment');
            scope.setLevel('info');
            scope.setContext('payment', {});
            Sentry.captureMessage('Redirect to payment');
        });

        await Sentry.flush(1000);
    } catch (error) {
        Sentry.captureException(error);
        console.warn('Error handling payment session:', error);
        return error;
    }

};

export const handleMembershipExtension = async ({ searchParams, formData, club, customerExtensionEndDate, cancelationReasonId, dataLayerData, onError, lang, additionalInformationId, additionalInformationValue }: HandleMembershipExtensionType) => {
    const modifiedSearchParams = new URLSearchParams(searchParams.toString());

    const successfulRedirectionSlug = club?.extensionThankYouPage?.slug;
    if (successfulRedirectionSlug) {
        modifiedSearchParams.set(
            URLParams.SUCCESSFUL_REGISTRATION_REDIRECT_SLUG,
            successfulRedirectionSlug
        );
    }

    try {
        const extensionFormValues = {
            ...formData,
            customerExtensionEndDate,
            cancelationReasonId,
            additionalInformationId,
            additionalInformationValue
        }

        const response = await apiPost<{ redirectUrl: string }>('/api/extension/extend-contract/', {
            searchParams: modifiedSearchParams.toString(),
            pathname: window.location.pathname,
            formValues: extensionFormValues,
            lang,
            dataLayerData: dataLayerData,
        });

        if (response?.redirectUrl) {
            window.location.href = response.redirectUrl;
            return true;
        }

        return false;
    } catch (error) {
        Sentry.captureException(error);
        console.error('Extension submission failed', error);
        onError?.()
        return false;
    }
}
